import { Injectable } from '@angular/core';
import { AppInjector } from '../../app-injector';
import { AuxActionData, IntegrationItemServiceInf, StatusString } from '../interfaces/integrations';
import { ConnectorConfigDialogComponent } from 'src/app/modules/integrations-applications/components/integ-config-dialogs/connector-config-dialog/connector-config-dialog.component';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { MatDialog } from '@angular/material/dialog';
import { AdminService } from 'src/app/core/services/admin.service';
import { IntegPolicyService } from 'src/app/core/services/integ-policy.service';
import { Account, WorkerPostRequest } from '@mitel/cloudlink-sdk';
import { AccountsService } from './accounts.service';
import { FeatureToggleService } from './feature-toggle.service';
import { SpinnerService } from './spinner.service';
import { WorkflowService } from './workflow.service';
import { RBACAuthGuardService } from '../guards/rbac-auth-guard.service';
import { ActionType, CategoryId } from '../utils/rbac.util';
import { StatusType } from '../constants/statusType.enum';
import { IntegMSTeamsToCLPresenceService } from './integ-msteams-to-cl-presence.service';
import { IntegPhonePresenceService } from './integ-phone-presence.service';
import { FeatureFlags } from '../interfaces/feature-flags';
import { MSTeamsSharedService } from './msteams-shared.service';
import { PresenceUpdateReason } from '../interfaces/msteams-presence-data';
import { MSTeamsAuxDialogComponent } from 'src/app/modules/integrations-applications/components/integ-aux-dialogs/msteams-aux-dialog/msteams-aux-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { MSTeamsInstallDialogComponent } from 'src/app/modules/integrations-applications/components/integ-config-dialogs/msteams-install-dialog/msteams-install-dialog.component';

const logContext = 'IntegMSTeamsService';

@Injectable({
	providedIn: 'root'
})

export class IntegMSTeamsService implements IntegrationItemServiceInf {

	private adminService: AdminService;
	private policyService: IntegPolicyService;
	private accountsService: AccountsService;
	private featureToggleService: FeatureToggleService;
	private spinnerService: SpinnerService;
	private workflowService: WorkflowService;
	public dialog: MatDialog;
	private rbacAuthGuardService: RBACAuthGuardService;
	private phonePresenceService: IntegPhonePresenceService;
	private msteamsToCLService: IntegMSTeamsToCLPresenceService;
	private msteamsSharedService: MSTeamsSharedService;
	private translateService: TranslateService;

	constructor(  ) 
	{
	}

	initServices()
	{
		if ( !this.adminService )
			this.adminService = AppInjector.injector.get(AdminService);

		if ( !this.policyService )
			this.policyService = AppInjector.injector.get(IntegPolicyService);

		if ( !this.accountsService )
			this.accountsService = AppInjector.injector.get(AccountsService);

		if ( !this.featureToggleService )
			this.featureToggleService = AppInjector.injector.get(FeatureToggleService);

		if ( !this.spinnerService )
			this.spinnerService = AppInjector.injector.get(SpinnerService);

		if ( !this.workflowService )
			this.workflowService = AppInjector.injector.get(WorkflowService);
	
		if ( !this.dialog )
			this.dialog = AppInjector.injector.get(MatDialog);
			
		if (!this.rbacAuthGuardService)
			this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);

		if (!this.phonePresenceService)
			this.phonePresenceService = AppInjector.injector.get(IntegPhonePresenceService);

		if (!this.msteamsToCLService)
			this.msteamsToCLService = AppInjector.injector.get(IntegMSTeamsToCLPresenceService);

		if (!this.msteamsSharedService)
			this.msteamsSharedService = AppInjector.injector.get(MSTeamsSharedService);

		if (!this.translateService)
			this.translateService = AppInjector.injector.get(TranslateService);
	}

	canBeSelected(account: Account): Promise<boolean>{
		this.initServices();

		let isDenied = this.rbacAuthGuardService.isPermissionDeniedForUser(CategoryId.INTEGRATIONMSTEAMS, ActionType.VIEW);
		return Promise.resolve(!isDenied);
	}

	getConfigurationDialog() : any
	{
		return ConnectorConfigDialogComponent;
	}

	getDynamicConfigurationDialog( account: Account ) : Promise<any>
	{
		this.initServices();
		return this.msteamsSharedService.isMSTeamsUpgradePending(account).then( async upgradeRequired => {
			if ( upgradeRequired == true )
				return MSTeamsInstallDialogComponent;
			else
				return ConnectorConfigDialogComponent;
		})
		.catch(error => {
			console.error("could not detect if there was a MS Teams feature update pending", error);
			return ConnectorConfigDialogComponent; // original dialog stands
		})
	}
	
	getConnectorData( account: Account ): Promise<any>
	{
		this.initServices();
		this.spinnerService.setSpinnerMessageByKey('app.loading');
		this.spinnerService.showSpinner();
		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			if ( tags && tags.MSTeams && tags.MSTeams.connectionId )
			{
				return this.workflowService.getMicrosoftConnector(tags.MSTeams.connectionId).then( result => {
					this.spinnerService.hideSpinner();
					return result;
				})
			}
			else
			{
				let data: any = {
					name: "",
					tenantId: "",
					clientId: "",
					clientSecret: "",
					connectionId: undefined
				}
				this.spinnerService.hideSpinner();
				return data;
			}
		});
	}

	saveConnectorData(account: Account, connectionData: any ): Promise<boolean>
	{
		this.initServices();
		// here we will create the connector 
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
		this.spinnerService.showSpinner();
		let dataChanges: any = {
			tags: {
				MSTeams: {
					setupComplete: true,
					connectionId: ""
				}
			}
		}
		return this.workflowService.saveMicrosoftConnector(connectionData).then(async data => {
			dataChanges.tags.MSTeams.connectionId = data.connectionId;
			this.msteamsSharedService.clearClientSecretCache();

			if ( connectionData.connectionId == undefined ) // need to update the tags
			{
				return this.adminService.updateAccount( account.accountId, dataChanges).then(result => {
					this.accountsService.mergeAccountWithModel(result);
					this.spinnerService.hideSpinner();
					return true;
				});
			}
			else
			{
				let promises = [];
				
				// the MS graph connection was updated. If there is a workflow we must request to update
				// the connector variables
				return this.adminService.getAccountTagsNoFrills( account.accountId ).then( tags => {
					// simplify the data for the next call. it expects the data in a different format
					let wfData: any = {
						connectionId: data.connectionId,
						clientId: data.authenticationSettings.properties.find((item) => item.name == 'client_id').value
					};

					if ( tags && tags.MSTeamsPresence && tags.MSTeamsPresence.activityId )
					{
						let presence1 = this.workflowService.updateMSTeamsPresenceWorkflowWithMSConnector(
							wfData, tags.MSTeamsPresence.activityId );
						promises.push(presence1);
					}

					// if the connector Id has changed, update the connector in the
					// Sync presence from MS Teams to CloudLink feature
					// in reality I do not think the connection Id changes
					if ( tags && tags.MSTeamsToCloudLink && 
						tags.MSTeamsToCloudLink.msGraphPresenceChangeWorkflow && 
						tags.MSTeamsToCloudLink.msGraphPresenceChangeWorkflow.workflowId &&
						(connectionData.connectionId != data.connectionId) )
					{
						let presence2 = this.workflowService.updateMSTeamsPresenceWorkflowWithMSConnectorIdOnly( data, 
							tags.MSTeamsToCloudLink.msGraphPresenceChangeWorkflow.workflowId);
						promises.push(presence2);
					}

					if ( tags && tags.MSTeamsToCloudLink && 
						tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow && 
						tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow.workflowId &&
						(connectionData.connectionId != data.connectionId) )
					{
						let presence3 = this.workflowService.updateMSTeamsPresenceWorkflowWithMSConnectorIdOnly( data, 
							tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow.workflowId);
						promises.push(presence3);
					}
					this.msteamsSharedService.clearAllMSTeamsToCLPresenceCache(); // change may cause a status change
					
					return Promise.all(promises).then( () => {
						this.spinnerService.hideSpinner();
						this.notifyMSTeamsToCloudLinkWorkflowOfMsGraphChange( tags );
						return true;
					}).catch(err => {
						this.spinnerService.hideSpinner();
						console.error(`${logContext}.saveConnectorData Error updating the microsoft connector in workflows, err=`, err);
						throw err;
					});
				})
				.catch(err => {
					this.spinnerService.hideSpinner();
					console.error(`${logContext}.saveConnectorDataError requesting account tags (they should exist) err=`, err);
					throw err;
				});
			}
		})
		.catch (err => {
			console.error(`${logContext}.saveConnectorData Error saving microsoft connector err=`, err);
			this.spinnerService.hideSpinner();
			throw err;
		})
	}

	notifyMSTeamsToCloudLinkWorkflowOfMsGraphChange( tags: any )
	{
		// only notify if the subfeature is enabled, otherwise it is not used
		if ( tags && tags.MSTeamsToCloudLink && tags.MSTeamsToCloudLink.enabled == true )
		{
			let reqBody: WorkerPostRequest = {
				inputs: { reason: PresenceUpdateReason.MS_GRAPH_CONNECTOR_CHANGE }
			}
			this.workflowService.executeWorkflowWithNoWait(tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow.workflowId, reqBody);
		}
	}

	isSetupComplete(account: Account) : Promise<boolean>
	{
		this.initServices();
		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			if ( tags && tags.MSTeams )
			{
				if ( tags.MSTeams.setupComplete == true )
				{
					return this.msteamsSharedService.isMSTeamsUpgradePending( account ).then( upgradePending => {
						if ( upgradePending )
							return false;
						else
							return true;
					})
					.catch (error => {
						console.error("could not detect if there was a MS Teams feature update pending", error);
						return true; // complete setup stands
					});
				}
				return false;
			}
			else
				return false;
		})
		.catch(err => {
			return false;
		})
	}

	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any>
	{
		this.initServices();
		if ( state == false )
		{
			this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
			this.spinnerService.showSpinner();

			return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( async newAccount => {

				this.msteamsSharedService.clearUpgradePendingCache( newAccount );
				// clean up the connector
				if ( newAccount.tags && newAccount.tags.MSTeams )
				{
					// only remove the connector, the child will remove the workflow
					try 
					{
						if ( newAccount.tags.MSTeams.connectionId )
							await this.workflowService.deleteMSTeamsConnector(newAccount.tags.MSTeams.connectionId);
					}
					catch (e)
					{
						// delete is best effort
						console.error(`${logContext}.handleIntegrationStateChange error during the deletion of the MS Teams connector, will continue deleting, err=`, e);
					}

					try 
					{
						if ( newAccount.tags && newAccount.tags.MSTeams )
							await this.adminService.deleteAccountTag( account.accountId, 'MSTeams' );
					}
					catch (e)
					{
						// delete is best effort
						console.error(`${logContext}.handleIntegrationStateChange error during the deletion of the MS Teams tags, err=`, e);
					}
				}			
				this.spinnerService.hideSpinner();
				return true;
			
			}).catch(err => {
				console.error(`${logContext}.handleIntegrationStateChange Failed to get the account, err=`, err);
				this.spinnerService.hideSpinner();
				throw err;
			});
		}
	}

	async getStatusString(account: Account): Promise<StatusString> {
		this.initServices();

		// no longer can just call the setup complete method
		let parentStatus: StatusString = {
			status: 'integrations.msteams.not-setup-error', 
			type: StatusType.caution
		}
		return this.adminService.getAccountTags(account.accountId).then(tags => {
			if ( tags && tags.MSTeams?.setupComplete != true ) 
			{
				return undefined; // there was no warning previously and there will be none now unless the original setup is done
			}
			// testing for an upgrade first
			return this.msteamsSharedService.isMSTeamsUpgradePending(account).then( async upgradeRequired => {
				if ( upgradeRequired == true )
				{
					parentStatus.type = StatusType.caution;
					parentStatus.status = 'integrations.msteams.feature-update-available';
					return parentStatus;
				}
				if ( tags && tags.MSTeams?.connectionId != undefined ) 
				{
					// check if the MS Graph connector is functioning
					return this.workflowService.getMicrosoftConnector(tags.MSTeams.connectionId).then(async ConnectorResult => {
						let isExpired = false;
						let missingPermission = false;
						try
						{
							isExpired = await this.msteamsSharedService.isMainConnectorClientSecretExpired(ConnectorResult);
						}
						catch (err)
						{
							missingPermission = this.msteamsSharedService.checkForMissingWFGraphPermission(err);
							// right now other errors are not displayed here
						}
						if ( isExpired ) 
						{
							parentStatus.type = StatusType.error;
							parentStatus.status = 'integrations.msteams.connector-client-secret-expired';
							return parentStatus;
						}

						if ( this.msteamsSharedService.isMainConnectorClientSecretExpiring() == true ) 
						{
							parentStatus.type = StatusType.caution;
							parentStatus.status = 'integrations.msteams.connector-client-secret-expiring';
							return parentStatus;
						}
						if ( ConnectorResult && (ConnectorResult.connected == false || ConnectorResult.enabled == false) ) 
						{
							parentStatus.type = StatusType.error;
							parentStatus.status = 'integrations.msteams.connector-error';
							return parentStatus;
						}
						if ( missingPermission ) 
						{
							parentStatus.type = StatusType.caution;
							parentStatus.status = 'integrations.msteams.connector-missing-permission';
							return parentStatus;
						}
						return this.phonePresenceService.isIntegrationStateOn( account ).then( isOn => {
							if ( isOn == false ) 
							{
								return parentStatus;
							}

							return this.msteamsToCLService.isIntegrationStateOn( account ).then( isOn => {
								if ( isOn == false ) 
								{
									return parentStatus;
								}

								return this.msteamsSharedService.getMSteamsToCLPresenceStatusString( account ).then( status => {
									if ( status != undefined ) 
									{
										// TBD - should we me more explicit here?
										parentStatus.type = status.type;
										parentStatus.status = 'integrations.msteams.subfeature-error';
										return parentStatus;

									}
									else
										// if we get here there was no error
										return undefined;
								});
							});
						})
						.catch( err => {
							console.error(`${logContext}.getStatusString Could not determine the subfeature status, err=`, err);
							return parentStatus;
						})
					})
				}
				else
				{
					parentStatus.type = StatusType.error;
					parentStatus.status = 'integrations.msteams.connector-error';
					return parentStatus;
				}
			});
		})
		.catch( err => {
			console.error(`${logContext}.getStatusString Could not determine integration status, err=`, err);
			parentStatus.type = StatusType.error;
			parentStatus.status = 'integrations.msteams.connector-error';
			return parentStatus;
		})
	}

	getConfigButtonLabel(account: Account): Promise<string>
	{
		this.initServices();
		return this.msteamsSharedService.isMSTeamsUpgradePending(account).then( async upgradeRequired => {
			if ( upgradeRequired == true )
			{
				return 'integrations.msteams.install-update-button';
			}
			else
				return 'integrations.complete-setup'; // due to the delay nature of returning this button, return a vale and not undefined
		})
		.catch( error => {
			console.error("Could not determine if there was an update pending, assume none", error);
			return 'integrations.complete-setup';
		})
	}

	isButtonDisabled(account: Account): Promise<boolean>
	{
		return Promise.resolve(false);
	}

	canDeleteIntegration(account: Account): Promise<boolean>
	{
		this.initServices();
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.REMOVE_MSTEAMS_CONFIRMATION } });
		return dialogRef.afterClosed().toPromise().then(result => {
			return result;
		});
	}

	getAuxAction(account: Account): Promise<any> 
	{
		this.initServices();

		let verifyStr;
		return this.adminService.getAccountTags(account.accountId).then(tags => {
			if ( tags && tags.MSTeams && tags.MSTeams.lastVerified ) 
			{
				let dateStr = new Date(tags.MSTeams.lastVerified).toLocaleDateString();
				verifyStr = this.translateService.instant( 'integrations.msteams.verify-statement', {dateStr: dateStr});
			}
			else
			{
				verifyStr = 'integrations.msteams.verify-statement-initial'
			}

			let actionData: AuxActionData = {
				statement: verifyStr,
				actionLabel: 'integrations.msteams.verify-action',
			};
			return Promise.resolve(actionData);
		});
	}

	async handleAuxAction(account: Account): Promise<any> 
	{
		this.initServices();

		// if we get here then we can show the Aux dialog
		let auxDialogRef = this.dialog.open(MSTeamsAuxDialogComponent, 
			{ 
				disableClose: true,
				autoFocus: false,
				minWidth: '700px',
				maxWidth: '975px',
				data: { account: account } 
			});
		await auxDialogRef.afterClosed().toPromise();
		return true;
	}
}
